import { Button, Flex } from 'antd';
import { useLayoutEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { deviceService } from '@/services/device-service';
import { IconWrapper } from '../ui/icon-wrapper';
import ErrorIcon from './icon/error.svg';
import SuccessIcon from './icon/success.svg';
import DeviceIcon from './icon/device.svg';
import DeviceOfflineIcon from './icon/device-offline.svg';
import { getPluginHelpCustomerConfig } from '@/constants/tip-config';
import { Modal } from '../antd-wrapper/modal/global/modal';
import { triggerCustomerModal } from '../antd-wrapper/modal/contact/customer-manager';
import { DeviceInfo } from '@/services/device-service/types';
import { DeviceConnectionStatus } from '@/store/modules/device-store';
import { GreenLoadingSvg } from '../ui/loading';
import { useNewStores } from '@/store/modules/agent-store';
import { useI18n } from '@/hooks/use-i18n';
import { i18n } from '@/i18n';

export function DeviceConnectionModel() {
  const { deviceStore } = useNewStores();
  const { t } = useI18n(['components', 'common']);
  const [pageStatus, setPageStatus] = useState<
    'getting' | 'idle' | 'loading' | 'tip:connection' | 'tip:disconnection'
  >('idle');
  const [pendingDeviceId, setPendingDeviceId] = useState<string>('');

  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isConnecting, setIsConnecting] = useState<string | null>(null);
  const isShow = deviceStore.isShowDeviceConnectionModel;

  const connectionStatus = deviceStore.connectionStatus;

  const title = useMemo(() => {
    if (pageStatus.startsWith('tip:')) return t('common:tip');
    return connectionStatus === 'connected:offline'
      ? t('components:deviceConnection.title.reconnect')
      : t('components:deviceConnection.title.connect');
  }, [connectionStatus, pageStatus, t]);

  const handleGetDeviceList = async () => {
    try {
      await deviceStore.flushDeviceInfo();
    } finally {
      setPageStatus('idle');
    }
  };

  const handleChangeSelectedDeviceId = async (device_id?: string) => {
    try {
      await deviceService.selectDevice(device_id);
      // const connectionStatus = deviceStore.connectionStatus;

      // // 选择成功！！！派发事件改变首页组
      // // 当前不是 connected:online 就要跳转到data_saver组
      // if (connectionStatus !== 'connected:online') {
      //   eventBus.emit(EventTypes.CHANGE_GROUP, 'data_saver');
      // }

      // 乐观更新
      deviceStore.selectedDeviceId = device_id;
      handleGetDeviceList();
    } catch (error) {
      console.log(error);
      toast.error(
        t('components:deviceConnection.message.updateDeviceIdFailed'),
      );
    } finally {
      setPageStatus('idle');
    }
  };

  const handleDisconnectDevice = async () => {
    setIsDisconnecting(true);
    try {
      await handleChangeSelectedDeviceId('');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const DeviceItem = ({
    device,
    onConnect,
    onDisconnect,
  }: {
    device: DeviceInfo;
    onConnect: (deviceId: string) => void;
    onDisconnect: () => void;
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const connectionStatusOfThisDevice = useMemo(() => {
      if (deviceStore.selectedDeviceId === device.device_id) {
        return deviceStore.connectionStatus;
      }
      return 'not-connected';
    }, [
      deviceStore.selectedDeviceId,
      deviceStore.currentDevice,
      device.device_id,
    ]);

    // 判断是否正在连接当前设备
    const isConnectingThisDevice = isConnecting === device.device_id;

    // 根据状态获取图标
    const deviceIcon = useMemo(() => {
      if (isConnectingThisDevice) {
        return GreenLoadingSvg;
      }

      switch (connectionStatusOfThisDevice) {
        case 'not-connected':
          return DeviceOfflineIcon;
        case 'connected:offline':
        case 'connected:online':
          return DeviceIcon;
        default:
          return DeviceOfflineIcon;
      }
    }, [isConnectingThisDevice, connectionStatusOfThisDevice]);

    return (
      <div
        onClick={() => {
          if (connectionStatusOfThisDevice !== 'connected:online') {
            onConnect(device.device_id);
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`w-full text-[14px] shrink-[0] h-[40px] flex justify-between items-center px-[12px] rounded-[8px] transition-colors duration-[200ms] hover:bg-[#F4F4F5] ${
          connectionStatusOfThisDevice === 'connected:online'
            ? undefined
            : 'cursor-pointer'
        }`}
      >
        <IconWrapper
          icon={deviceIcon}
          size={24}
          className="mr-[6px]"
          spin={isConnectingThisDevice}
        />

        <span className="flex-1 truncate">{device.device_name}</span>
        <DeviceStatus
          connectionStatus={connectionStatusOfThisDevice}
          isHovered={isHovered}
          onDisconnect={onDisconnect}
        />
      </div>
    );
  };

  const DeviceStatus = ({
    isHovered,
    onDisconnect,
    connectionStatus,
  }: {
    isHovered: boolean;
    onDisconnect: () => void;
    connectionStatus: DeviceConnectionStatus;
  }) => {
    switch (connectionStatus) {
      case 'not-connected':
        return null;
      case 'connected:offline':
        if (isHovered) {
          return (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onDisconnect();
              }}
              className="text-[12px] text-[#EE7171] font-normal cursor-pointer hover:text-[#D85555] transition-colors"
            >
              {t('components:deviceConnection.status.disconnect')}
            </span>
          );
        }
        return (
          <div className="rounded-full px-[6px] py-[2px] flex items-center text-[12px] font-semibold bg-[rgba(238,113,113,0.2)] text-[#ee7171] gap-[4px]">
            <IconWrapper icon={ErrorIcon} size={12} />
            <span>
              {t('components:deviceConnection.status.connectionError')}
            </span>
          </div>
        );

      case 'connected:online':
        if (isHovered) {
          return (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onDisconnect();
              }}
              className="text-[12px] text-[#7171EE] font-normal cursor-pointer hover:text-[#5959CB] transition-colors"
            >
              {t('components:deviceConnection.status.disconnect')}
            </span>
          );
        }
        return (
          <div className="rounded-full px-[6px] py-[2px] flex items-center text-[12px] font-semibold bg-[rgba(113,113,238,0.2)] text-[#7171ee] gap-[4px]">
            <IconWrapper icon={SuccessIcon} size={12} />
            <span>{t('components:deviceConnection.status.connected')}</span>
          </div>
        );

      default:
        return null;
    }
  };

  const MainContent = useMemo(() => {
    return (
      <div
        className={`flex w-full px-[36px] flex-col justify-center ${pageStatus === 'idle' && deviceStore.currentDevice.length ? '' : 'h-full'}`}
      >
        {pageStatus === 'tip:disconnection' && (
          <span className="text-[16px]">
            {t('components:deviceConnection.message.disconnectConfirm')}
          </span>
        )}
        {pageStatus === 'tip:connection' && (
          <span className="text-[16px]">
            {t('components:deviceConnection.message.connectConfirm')}
          </span>
        )}

        {pageStatus === 'loading' && (
          <span className="text-[14px] text-center font-weight-[600]">
            {t('components:deviceConnection.message.detecting')}
          </span>
        )}

        {pageStatus === 'idle' && deviceStore.currentDevice.length === 0 && (
          <div className="text-[14px] text-center font-weight-[600]">
            {t('components:deviceConnection.message.noDeviceDetected')}
          </div>
        )}

        {pageStatus === 'idle' && deviceStore.currentDevice.length > 0 && (
          <>
            <span className="text-[14px] text-[#00000073] mb-[17px] mt-[24px] block">
              {t('components:deviceConnection.message.selectDevice')}
            </span>
            {deviceStore.currentDevice.map((device: DeviceInfo) => (
              <DeviceItem
                key={device.device_id}
                device={device}
                onConnect={async (deviceId) => {
                  if (deviceId === deviceStore.selectedDeviceId) {
                    return;
                  }
                  setPendingDeviceId(deviceId);
                  setPageStatus('tip:connection');
                }}
                onDisconnect={async () => {
                  if (connectionStatus === 'connected:offline') {
                    await handleDisconnectDevice();
                  } else {
                    setPageStatus('tip:disconnection');
                  }
                }}
              />
            ))}
          </>
        )}
      </div>
    );
  }, [
    pageStatus,
    deviceStore.currentDevice,
    deviceStore.selectedDeviceId,
    connectionStatus,
    pendingDeviceId,
    isConnecting,
    t,
  ]);

  const Footer = () => {
    if (pageStatus === 'tip:disconnection') {
      return (
        <Flex gap={16} align="center">
          <div className="flex-1" />
          <Button
            onClick={() => {
              setPageStatus('idle');
            }}
            size="large"
            type="text"
          >
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleDisconnectDevice}
            size="large"
            type="primary"
            className="ms-0"
            loading={isDisconnecting}
          >
            {t('common:confirm')}
          </Button>
        </Flex>
      );
    }
    if (pageStatus === 'tip:connection') {
      return (
        <Flex gap={16} align="center">
          <div className="flex-1" />
          <Button
            onClick={() => {
              setPageStatus('idle');
              setPendingDeviceId('');
            }}
            size="large"
            type="text"
          >
            {t('common:cancel')}
          </Button>
          <Button
            onClick={async () => {
              try {
                setIsConnecting(pendingDeviceId);
                setPageStatus('idle');
                await handleChangeSelectedDeviceId(pendingDeviceId);
              } finally {
                setIsConnecting(null);
                setPendingDeviceId('');
              }
            }}
            size="large"
            type="primary"
            className="ms-0"
            loading={isConnecting !== null}
          >
            {t('common:confirm')}
          </Button>
        </Flex>
      );
    }
    if (deviceStore.currentDevice.length === 0 && pageStatus !== 'loading') {
      return (
        <Flex align="center">
          <Button
            type="link"
            onClick={() => {
              const currentLang = i18n.language;
              const url =
                currentLang === 'zh-CN'
                  ? 'https://docs.qq.com/doc/DT3FZT09Zd254c1Bv?nlc=1'
                  : 'https://mcn1x2r9j128.feishu.cn/wiki/VTtiwWnowiCwgDkzFRScV4uenQd?from=from_copylink';
              window.open(url, '_blank');
            }}
            style={{
              textDecoration: 'underline',
              padding: 0,
            }}
            className="text-[#7171EE] text-[14px] transition-colors duration-200"
          >
            {t('components:deviceConnection.button.whatShouldIDo')}
          </Button>
          <div className="flex-1" />
          <Button
            onClick={() => {
              deviceStore.isShowDeviceConnectionModel = false;
              const config = getPluginHelpCustomerConfig();
              triggerCustomerModal({
                title: config.title,
                data: config.data,
              });
            }}
            size="large"
            type="text"
          >
            {t('components:deviceConnection.button.contactCustomer')}
          </Button>
          <Button
            className="ml-[16px]"
            onClick={() => {
              setPageStatus('loading');
              handleGetDeviceList();
            }}
            size="large"
            type="primary"
          >
            {t('common:retry')}
          </Button>
        </Flex>
      );
    }
    if (connectionStatus === 'connected:offline' && pageStatus === 'idle') {
      return (
        <Flex align="center">
          <Button
            type="link"
            onClick={() => {
              const currentLang = i18n.language;
              const url =
                currentLang === 'zh-CN'
                  ? 'https://docs.qq.com/doc/DT3FZT09Zd254c1Bv?nlc=1'
                  : 'https://mcn1x2r9j128.feishu.cn/wiki/VTtiwWnowiCwgDkzFRScV4uenQd?from=from_copylink';
              window.open(url, '_blank');
            }}
          >
            {t('components:deviceConnection.button.whatShouldIDo')}
          </Button>
        </Flex>
      );
    }

    return null;
  };

  useLayoutEffect(() => {
    if (isShow) {
      setPageStatus('loading');
      handleGetDeviceList();
    }
  }, [isShow]);

  return (
    <Modal
      headerHeight={56}
      open={isShow}
      onCancel={() => {
        deviceStore.isShowDeviceConnectionModel = false;
        setIsConnecting(null);
        setPendingDeviceId('');
        setPageStatus('idle');
      }}
      title={title}
      styles={{
        footer: {
          margin: 0,
        },
      }}
      footer={Footer}
    >
      {MainContent}
    </Modal>
  );
}
